// Native (Android/iOS) permanent image cache: every /api/media image is saved
// to the phone's storage once and served from disk afterwards. Works at the
// HTMLImageElement.src *property* level so React's `img.src = url` is caught
// (setAttribute alone misses React's reconciler).
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { mediaSrc } from "./api";

const DIR = "img-cache-v2";
const OLD_DIRS = ["img-cache"];
const index = new Set();
const recent = new Map();
const saving = new Set();

const isMedia = (u) => typeof u === "string" && u.includes("/api/media/");

const keyOf = (u) => {
  let s = u;
  try { const x = new URL(u, window.location.href); s = x.pathname + x.search; } catch { /* keep raw */ }
  let a = 5381, b = 2166136261;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = ((a << 5) + a + c) >>> 0;
    b = Math.imul(b ^ c, 16777619) >>> 0;
  }
  return `${a.toString(36)}${b.toString(36)}`;
};

const forget = (k) => {
  index.delete(k);
  recent.delete(k);
  Filesystem.deleteFile({ path: `${DIR}/${k}`, directory: Directory.Data }).catch(() => {});
};

const remember = (k, dataUrl) => {
  recent.set(k, dataUrl);
  if (recent.size > 150) recent.delete(recent.keys().next().value);
};

async function readCached(k) {
  const r = await Filesystem.readFile({ path: `${DIR}/${k}`, directory: Directory.Data, encoding: Encoding.UTF8 });
  const raw = String(r.data);
  const sep = raw.indexOf("|");
  const mime = raw.slice(0, sep);
  const b64 = raw.slice(sep + 1);
  if (sep < 6 || !mime.startsWith("image/") || b64.length < 32) throw new Error("corrupt");
  const dataUrl = `data:${mime};base64,${b64}`;
  remember(k, dataUrl);
  return dataUrl;
}

const toBase64 = (blob) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(String(fr.result).split(",")[1] || "");
  fr.onerror = rej;
  fr.readAsDataURL(blob);
});

async function saveToDisk(url, k) {
  if (saving.has(k) || index.has(k)) return;
  saving.add(k);
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 25000);
    const res = await fetch(url, { cache: "force-cache", signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return;
    const b64 = await toBase64(blob);
    await Filesystem.writeFile({
      path: `${DIR}/${k}`, data: `${blob.type}|${b64}`,
      directory: Directory.Data, encoding: Encoding.UTF8, recursive: true,
    });
    remember(k, `data:${blob.type};base64,${b64}`);
    index.add(k);
  } catch { /* offline or disk full — image still shows from network */ }
  finally { saving.delete(k); }
}

const queue = [];
let active = 0;
const pump = () => {
  while (active < 4 && queue.length) {
    const u = queue.shift();
    active++;
    saveToDisk(mediaSrc(u), keyOf(u)).finally(() => { active--; pump(); });
  }
};

// Download images straight to phone storage before the user opens them.
export function prefetchImages(urls = []) {
  if (!Capacitor.isNativePlatform()) return;
  for (const u of urls) {
    if (!isMedia(u)) continue;
    const k = keyOf(u);
    if (index.has(k) || saving.has(k) || queue.some((q) => keyOf(q) === k)) continue;
    queue.push(u);
  }
  pump();
}

export function initNativeImageCache() {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("Filesystem")) return Promise.resolve();
  OLD_DIRS.forEach((d) => Filesystem.rmdir({ path: d, directory: Directory.Data, recursive: true }).catch(() => {}));
  const ready = Filesystem.readdir({ path: DIR, directory: Directory.Data })
    .then(({ files }) => files.forEach((f) => index.add(typeof f === "string" ? f : f.name)))
    .catch(() => Filesystem.mkdir({ path: DIR, directory: Directory.Data, recursive: true }).catch(() => {}));

  const showCached = (img, url, k, dataUrl) => {
    if (img.__khSrc !== url) return;
    img.addEventListener("error", () => {
      if (img.__khSrc !== url) return;
      forget(k);
      origSet.call(img, url);
    }, { once: true });
    origSet.call(img, dataUrl);
  };

  const applyCached = (img, url) => {
    const k = keyOf(url);
    img.__khSrc = url;
    if (recent.has(k)) { showCached(img, url, k, recent.get(k)); return; }
    if (index.has(k)) {
      readCached(k)
        .then((d) => showCached(img, url, k, d))
        .catch(() => { forget(k); if (img.__khSrc === url) origSet.call(img, url); });
      return;
    }
    img.addEventListener("load", () => saveToDisk(url, k), { once: true });
    origSet.call(img, url);
  };

  // Intercept the `src` property setter (React assigns via property, not
  // setAttribute). This is the fix that makes every image on every screen
  // served from disk cache after the first fetch.
  const desc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, "src");
  const origSet = desc && desc.set;
  if (origSet) {
    Object.defineProperty(HTMLImageElement.prototype, "src", {
      configurable: true,
      enumerable: true,
      get() { return desc.get.call(this); },
      set(value) {
        if (!isMedia(value)) return origSet.call(this, value);
        applyCached(this, value);
      },
    });
  }

  // Also override setAttribute (some libs still use it)
  const origSetAttr = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (name, value) {
    if (this.tagName !== "IMG" || name !== "src" || !isMedia(value)) return origSetAttr.call(this, name, value);
    applyCached(this, value);
  };
  return ready;
}
